import React from "react";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import Player from "core/components/Player";
import Skeleton from "@mui/material/Skeleton";
import { trackWidget } from "core/utils/tracker";
import { NextArenaContentQuery } from "arena/queries";
import { useQuery, useSubscription } from "@apollo/client";
import { ContentStreamStatusChangedSubscription } from "core/subscriptions";
import { usePlayerFullscreen } from "core/components/PlayerFullscreenProvider";
import { useLocation } from "react-router-dom";

const Root = styled("div")({
  overflow: "hidden",
});

const StyledPlayer = styled("div", {
  shouldForwardProp: (prop) => prop !== "isPlayerFullscreen",
})(({ theme, isPlayerFullscreen }) => ({
  width: isPlayerFullscreen ? "100vw" : "100%",
  height: isPlayerFullscreen ? "100vh" : 0,
  paddingTop: isPlayerFullscreen ? 0 : "56.25%",
  position: isPlayerFullscreen ? "fixed" : "relative",
  top: isPlayerFullscreen ? 0 : "auto",
  left: isPlayerFullscreen ? 0 : "auto",
  zIndex: isPlayerFullscreen ? 9999 : 1,
  background: theme.palette.mode === "dark" ? "black" : "white",
  overflow: "hidden",

  "&:fullscreen": {
    paddingTop: 0,
  },
}));

const StyledSkeleton = styled(Skeleton)(({ theme }) => ({
  width: "100%",
  zIndex: 1,
  position: "relative",
  height: 0,
  paddingTop: "56.25%",
  background: theme.palette.mode === "dark" ? "black" : "white  ",
  overflow: "hidden",

  [theme.breakpoints.up("sm")]: {
    borderRadius: 4,
  },
}));

const sponsorPlayerLogoPlaces = {
  VIDEO: "VIDEO_ON_SITE",
  STREAM: "STREAM_ON_SITE",
  // SHORTY: undefined - для шортисов не нужны логотипы плеера
};

const sponsorPlayerButtonPlaces = {
  VIDEO: "VIDEO_ON_SITE",
  STREAM: "STREAM_ON_SITE",
  // SHORTY: undefined - для шортисов не нужны кнопки плеера
};

const sponsorVideoPlaces = {
  VIDEO: "VIDEO_ON_SITE",
  STREAM: "STREAM_ON_SITE",
  SHORTY: "SHORTY_ON_SITE", // Будет формировать PREROLL_IN_SHORTY_ON_SITE и POSTROLL_IN_SHORTY_ON_SITE
};

const sponsorChatMessagePlaces = {
  STREAM: "STREAM_ON_SITE",
  // SHORTY не поддерживает чат
};

const sponsorInStreamBannerPlaces = {
  VIDEO: "VIDEO_ON_SITE",
  STREAM: "STREAM_ON_SITE",
  // SHORTY: undefined - для шортисов не нужны in-stream баннеры
};

const sponsorCornerBannerPlaces = {
  VIDEO: "VIDEO_ON_SITE",
  STREAM: "STREAM_ON_SITE",
  // SHORTY: undefined - для шортисов не нужны угловые баннеры
};

const STREAM = "STREAM";

export default function ArenaTv({
  autoPlay,
  loop,
  muted,
  contentId,
  rightholderIds,
}) {
  const onCompleted = (data) => {
    if (data?.nextArenaContent) {
      if (data.nextArenaContent.widget) {
        trackWidget(data.nextArenaContent.widget, "HIT");
      }
    }
  };

  const variables = {};

  if (contentId) {
    variables.id = contentId;
  }

  if (rightholderIds?.length) {
    variables.rightholderIds = rightholderIds;
  }

  const location = useLocation();
  // Диалог (видео/канал/стрим) открыт поверх страницы: страница остаётся
  // смонтированной, и фоновый плеер продолжал бы играть под диалогом.
  const isRouteDialogOpen = Boolean(location?.state?.background);

  const { data, loading, error, refetch } = useQuery(NextArenaContentQuery, {
    variables,
    skip: !process.browser,
    fetchPolicy: "network-only",
    onCompleted,
  });

  const content = data?.nextArenaContent;

  useSubscription(ContentStreamStatusChangedSubscription, {
    skip: !content || !process.browser || content?.type !== STREAM,
    onData: ({ client, data }) => {
      if (data?.data?.contentStreamStatusChanged?.rejectModeration) {
        client.resetStore();
      }
    },
    variables: {
      id: content?.id,
    },
  });

  if (loading || !process.browser) {
    return <StyledSkeleton variant="rectangular" />;
  }

  if (!content) {
    return null;
  }

  if (error) {
    return null;
  }

  const sponsorPlayerLogoPlace = sponsorPlayerLogoPlaces[content.type];
  const sponsorPlayerButtonPlace = sponsorPlayerButtonPlaces[content.type];
  const sponsorVideoPlace = sponsorVideoPlaces[content.type];
  const sponsorChatMessagePlace = sponsorChatMessagePlaces[content.type];
  const sponsorInStreamBannerPlace = sponsorInStreamBannerPlaces[content.type];
  const sponsorCornerBannerPlace = sponsorCornerBannerPlaces[content.type];
  const { isPlayerFullscreen } = usePlayerFullscreen();

  const onEnded = () => {
    refetch();
  };

  if (isRouteDialogOpen) {
    return (
      <Root>
        <StyledSkeleton variant="rectangular" />
      </Root>
    );
  }

  return (
    <Root>
      <StyledPlayer id="arena-player" isPlayerFullscreen={isPlayerFullscreen}>
        <Player
          loop={loop}
          muted={muted}
          autoPlay={autoPlay}
          content={content}
          key={content.id}
          hideRelatedContent
          hideShare
          hideLogo
          updateVideoPosition
          onEnded={onEnded}
          fullscreenTarget="arena-player"
          sponsorCornerBannerPlace={sponsorCornerBannerPlace}
          sponsorInStreamBannerPlace={sponsorInStreamBannerPlace}
          sponsorPlayerLogoPlace={sponsorPlayerLogoPlace}
          sponsorPlayerButtonPlace={sponsorPlayerButtonPlace}
          sponsorVideoPlace={sponsorVideoPlace}
          sponsorChatMessagePlace={sponsorChatMessagePlace}
        />
      </StyledPlayer>
    </Root>
  );
}

ArenaTv.propTypes = {
  arena: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  contentId: PropTypes.string,
  autoPlay: PropTypes.bool,
  loop: PropTypes.bool,
  muted: PropTypes.bool,
  rightholderIds: PropTypes.arrayOf(PropTypes.string),
};
